/**
 * Date utilities for the DatePickerDialog component
 */

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
  timezone?: string;
}

export interface DateRangePreset {
  id: string;
  label: string;
  description?: string;
  getValue: (timezone?: string) => DateRange;
  icon?: string;
  category?: 'common' | 'business' | 'year' | 'custom';
}

export interface SavedRange {
  id: string;
  name: string;
  emoji?: string;
  range: DateRange;
  createdAt: Date;
}

export interface TimeRange {
  hours: number;
  minutes: number;
  seconds?: number;
}

/**
 * Format date for display based on locale and timezone
 */
export function formatDate(
  date: Date | string | null,
  options: {
    locale?: string;
    timezone?: string;
    format?: 'short' | 'medium' | 'long' | 'time';
    includeTime?: boolean;
  } = {}
): string {
  if (!date) return '';

  // Defensive: Convert string to Date if needed
  const dateObj = date instanceof Date ? date : new Date(date);

  // Validate date is valid
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const {
    locale = 'en-US',
    timezone = 'Europe/Istanbul',
    format = 'short',
    includeTime = false,
  } = options;

  const formatOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };

  if (includeTime || format === 'time') {
    formatOptions.hour = '2-digit';
    formatOptions.minute = '2-digit';
  }

  if (format === 'long') {
    formatOptions.weekday = 'long';
    formatOptions.month = 'long';
  } else if (format === 'medium') {
    formatOptions.month = 'short';
  }

  return new Intl.DateTimeFormat(locale, formatOptions).format(dateObj);
}

/**
 * Format date range for display
 */
export function formatDateRange(
  range: DateRange,
  options: {
    locale?: string;
    timezone?: string;
    separator?: string;
    includeTime?: boolean;
  } = {}
): string {
  const { separator = ' - ', locale = 'en-US', timezone, includeTime = false } = options;

  if (!range.startDate && !range.endDate) return '';

  const start = range.startDate
    ? formatDate(range.startDate, { locale, timezone, includeTime })
    : '';
  const end = range.endDate ? formatDate(range.endDate, { locale, timezone, includeTime }) : '';

  if (start && end) {
    return `${start}${separator}${end}`;
  }
  if (start) {
    return `${includeTime ? (locale === 'tr-TR' ? 'Şu andan itibaren: ' : 'From ') : ''}${start}`;
  }
  if (end) {
    return `${includeTime ? (locale === 'tr-TR' ? 'Şu ana kadar: ' : 'Until ') : ''}${end}`;
  }

  return '';
}

/**
 * Check if a date is within a range
 */
export function isDateInRange(date: Date, range: DateRange): boolean {
  if (!range.startDate && !range.endDate) return true;
  if (range.startDate && date < range.startDate) return false;
  if (range.endDate && date > range.endDate) return false;
  return true;
}

/**
 * Get the number of days between two dates
 */
export function getDaysBetween(start: Date, end: Date): number {
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Add days to a date
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Add months to a date
 */
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

/**
 * Add years to a date
 */
export function addYears(date: Date, years: number): Date {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
}

/**
 * Get start of day
 */
export function startOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get end of day
 */
export function endOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Get start of month
 */
export function startOfMonth(date: Date): Date {
  const result = new Date(date);
  result.setDate(1);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get end of month
 */
export function endOfMonth(date: Date): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + 1, 0);
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Get start of quarter
 */
export function startOfQuarter(date: Date): Date {
  const quarter = Math.floor(date.getMonth() / 3);
  const result = new Date(date);
  result.setMonth(quarter * 3, 1);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get end of quarter
 */
export function endOfQuarter(date: Date): Date {
  const quarter = Math.floor(date.getMonth() / 3);
  const result = new Date(date);
  result.setMonth((quarter + 1) * 3, 0);
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Get start of year
 */
export function startOfYear(date: Date): Date {
  const result = new Date(date);
  result.setMonth(0, 1);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get end of year
 */
export function endOfYear(date: Date): Date {
  const result = new Date(date);
  result.setMonth(11, 31);
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Get year to date
 */
export function yearToDate(timezone?: string): DateRange {
  const now = new Date();
  return {
    startDate: startOfYear(now),
    endDate: now,
    timezone,
  };
}

/**
 * Get fiscal year range (assuming fiscal year starts in April)
 */
export function fiscalYear(timezone?: string): DateRange {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const startDate = new Date(year, 3, 1); // April 1st
  const endDate = new Date(year + 1, 2, 31); // March 31st
  return {
    startDate,
    endDate,
    timezone,
  };
}

/**
 * Get last fiscal year
 */
export function lastFiscalYear(timezone?: string): DateRange {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() - 1 : now.getFullYear() - 2;
  const startDate = new Date(year, 3, 1); // April 1st
  const endDate = new Date(year + 1, 2, 31); // March 31st
  return {
    startDate,
    endDate,
    timezone,
  };
}

/**
 * Create preset ranges
 */
export function createPresetRanges(locale: string = 'tr-TR'): DateRangePreset[] {
  const isTurkish = locale.startsWith('tr');

  return [
    // Common ranges
    {
      id: 'today',
      label: isTurkish ? 'Bugün' : 'Today',
      description: isTurkish ? 'Sadece bugünün verileri' : "Today's data only",
      category: 'common',
      getValue: (timezone) => {
        const today = new Date();
        return {
          startDate: startOfDay(today),
          endDate: endOfDay(today),
          timezone,
        };
      },
    },
    {
      id: 'yesterday',
      label: isTurkish ? 'Dün' : 'Yesterday',
      description: isTurkish ? 'Dünün verileri' : "Yesterday's data only",
      category: 'common',
      getValue: (timezone) => {
        const yesterday = addDays(new Date(), -1);
        return {
          startDate: startOfDay(yesterday),
          endDate: endOfDay(yesterday),
          timezone,
        };
      },
    },
    {
      id: 'last7days',
      label: isTurkish ? 'Son 7 gün' : 'Last 7 days',
      description: isTurkish ? 'Son 7 günlük veriler' : 'Last 7 days of data',
      category: 'common',
      getValue: (timezone) => {
        const end = endOfDay(new Date());
        const start = startOfDay(addDays(end, -6));
        return { startDate: start, endDate: end, timezone };
      },
    },
    {
      id: 'last30days',
      label: isTurkish ? 'Son 30 gün' : 'Last 30 days',
      description: isTurkish ? 'Son 30 günlük veriler' : 'Last 30 days of data',
      category: 'common',
      getValue: (timezone) => {
        const end = endOfDay(new Date());
        const start = startOfDay(addDays(end, -29));
        return { startDate: start, endDate: end, timezone };
      },
    },
    // Business periods
    {
      id: 'thisMonth',
      label: isTurkish ? 'Bu ay' : 'This month',
      description: isTurkish ? 'Bu ayın başından bugüne' : 'From start of this month',
      category: 'business',
      getValue: (timezone) => {
        const now = new Date();
        return {
          startDate: startOfMonth(now),
          endDate: now,
          timezone,
        };
      },
    },
    {
      id: 'lastMonth',
      label: isTurkish ? 'Geçen ay' : 'Last month',
      description: isTurkish ? 'Geçen ayın tamamı' : 'Entire last month',
      category: 'business',
      getValue: (timezone) => {
        const now = new Date();
        const end = endOfMonth(addMonths(now, -1));
        const start = startOfMonth(end);
        return { startDate: start, endDate: end, timezone };
      },
    },
    {
      id: 'thisQuarter',
      label: isTurkish ? 'Bu çeyrek' : 'This quarter',
      description: isTurkish ? 'Bu çeyreğin başından bugüne' : 'From start of this quarter',
      category: 'business',
      getValue: (timezone) => {
        const now = new Date();
        return {
          startDate: startOfQuarter(now),
          endDate: now,
          timezone,
        };
      },
    },
    {
      id: 'lastQuarter',
      label: isTurkish ? 'Geçen çeyrek' : 'Last quarter',
      description: isTurkish ? 'Geçen çeyreğin tamamı' : 'Entire last quarter',
      category: 'business',
      getValue: (timezone) => {
        const now = new Date();
        const end = endOfQuarter(addMonths(now, -3));
        const start = startOfQuarter(end);
        return { startDate: start, endDate: end, timezone };
      },
    },
    // Year ranges
    {
      id: 'thisYear',
      label: isTurkish ? 'Bu yıl' : 'This year',
      description: isTurkish ? 'Bu yılın başından bugüne' : 'From start of this year',
      category: 'year',
      getValue: (timezone) => {
        const now = new Date();
        return {
          startDate: startOfYear(now),
          endDate: now,
          timezone,
        };
      },
    },
    {
      id: 'lastYear',
      label: isTurkish ? 'Geçen yıl' : 'Last year',
      description: isTurkish ? 'Geçen yılın tamamı' : 'Entire last year',
      category: 'year',
      getValue: (timezone) => {
        const now = new Date();
        const end = endOfYear(addYears(now, -1));
        const start = startOfYear(end);
        return { startDate: start, endDate: end, timezone };
      },
    },
    {
      id: 'yearToDate',
      label: isTurkish ? 'Yıl başından bugüne' : 'Year to date',
      description: isTurkish ? "1 Ocak'tan bugüne" : 'From January 1st to today',
      category: 'year',
      getValue: yearToDate,
    },
    // Custom business ranges
    {
      id: 'fiscalYear',
      label: isTurkish ? 'Mali yıl' : 'Fiscal year',
      description: isTurkish ? "1 Nisan'dan itibaren" : 'From April 1st',
      category: 'custom',
      getValue: fiscalYear,
    },
    {
      id: 'lastFiscalYear',
      label: isTurkish ? 'Geçen mali yıl' : 'Last fiscal year',
      description: isTurkish ? 'Geçen mali yılın tamamı' : 'Entire last fiscal year',
      category: 'custom',
      getValue: lastFiscalYear,
    },
    {
      id: 'last365days',
      label: isTurkish ? 'Son 365 gün' : 'Last 365 days',
      description: isTurkish ? 'Son bir yıllık veriler' : 'Last year of data',
      category: 'custom',
      getValue: (timezone) => {
        const end = endOfDay(new Date());
        const start = startOfDay(addDays(end, -364));
        return { startDate: start, endDate: end, timezone };
      },
    },
  ];
}

/**
 * Validate date range
 */
export function validateDateRange(range: DateRange): {
  isValid: boolean;
  error?: string;
} {
  if (!range.startDate && !range.endDate) {
    return { isValid: false, error: 'Lütfen en az bir tarih seçin' };
  }

  if (range.startDate && range.endDate && range.startDate > range.endDate) {
    return { isValid: false, error: 'Başlangıç tarihi bitiş tarihinden sonra olamaz' };
  }

  // Check if range is too large (e.g., more than 10 years)
  if (range.startDate && range.endDate) {
    const daysDiff = getDaysBetween(range.startDate, range.endDate);
    if (daysDiff > 3650) {
      return { isValid: false, error: 'Tarih aralığı 10 yıldan uzun olamaz' };
    }
  }

  return { isValid: true };
}

/**
 * Get comparison period for a given range
 */
export function getComparisonPeriod(
  range: DateRange,
  type: 'previous' | 'lastYear' = 'previous'
): DateRange {
  if (!range.startDate || !range.endDate) {
    return { startDate: null, endDate: null };
  }

  const daysDiff = getDaysBetween(range.startDate, range.endDate);

  if (type === 'previous') {
    const endDate = addDays(range.startDate, -1);
    const startDate = addDays(endDate, -daysDiff);
    return { startDate, endDate };
  } else {
    const startDate = addYears(range.startDate, -1);
    const endDate = addYears(range.endDate, -1);
    return { startDate, endDate };
  }
}

/**
 * Calculate percentage change between two ranges
 */
export function calculatePercentageChange(
  current: number,
  previous: number
): {
  change: number;
  percentage: number;
  isPositive: boolean;
} {
  if (previous === 0) {
    return {
      change: current,
      percentage: current > 0 ? 100 : 0,
      isPositive: current > 0,
    };
  }

  const change = current - previous;
  const percentage = Math.abs((change / previous) * 100);

  return {
    change,
    percentage: Math.round(percentage * 100) / 100,
    isPositive: change >= 0,
  };
}

/**
 * Convert time string to TimeRange
 */
export function parseTime(time: string): TimeRange | null {
  const match = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;

  return {
    hours: parseInt(match[1]),
    minutes: parseInt(match[2]),
    seconds: match[3] ? parseInt(match[3]) : 0,
  };
}

/**
 * Format TimeRange to string
 */
export function formatTime(time: TimeRange): string {
  const hours = time.hours.toString().padStart(2, '0');
  const minutes = time.minutes.toString().padStart(2, '0');
  const seconds = time.seconds !== undefined ? `:${time.seconds.toString().padStart(2, '0')}` : '';
  return `${hours}:${minutes}${seconds}`;
}

/**
 * Quick time presets
 */
export const timePresets = [
  { label: 'Başlangıç', value: { hours: 0, minutes: 0, seconds: 0 } },
  { label: 'Bitiş', value: { hours: 23, minutes: 59, seconds: 59 } },
  {
    label: 'Şimdi',
    value: () => {
      const now = new Date();
      return { hours: now.getHours(), minutes: now.getMinutes(), seconds: now.getSeconds() };
    },
  },
];
